import { ThunkType } from 'app/hooks/appHooks';
import { mapCityDetailResponse } from 'app/functions';
import { SearchMaskDirection, SearchMaskLocation } from 'app/models/types/store/location';
import {
  arrivalCitySelector,
  currentProductTypesExtendedSelector,
  currentProductTypesSelector,
  optionsSelector,
  showStationSuggestionSelector,
} from 'app/store/selectors';
import { fetchCityRelationDetails } from 'app/providers';
import {
  updateSearchLocation as updateSearchLocationAction,
  updateAutocompleteValue,
} from 'app/store/reducers/location';
import { setLocation } from 'app/store/thunk/location/setLocationThunk';
import { requestSearch } from 'app/store/thunk/general';
import { resetAllProducts, setRelationProducts } from 'app/store/reducers/product';
import { requestAutocomplete } from './requestAutocompleteThunk';

export type UpdateSearchLocationType = {
  direction: SearchMaskDirection;
  location: SearchMaskLocation;
  previousLocations?: {
    arrivalCity: SearchMaskLocation;
    departureCity: SearchMaskLocation;
  };
  isAutoUpdate?: boolean;
  isInit?: boolean;
};

export const updateSearchLocation =
  ({
    direction,
    location,
    previousLocations,
    isAutoUpdate,
    isInit,
  }: UpdateSearchLocationType): ThunkType<void> =>
  async (dispatch, getState) => {
    dispatch(
      updateSearchLocationAction({ direction, location, previousLocations, isAutoUpdate, isInit }),
    );

    const state = getState();
    const showStationSuggestions: boolean = showStationSuggestionSelector(state);
    const { citiesDetailsEndpoint, locale }: ReturnType<typeof optionsSelector> =
      optionsSelector(state);
    const previouslySelectedLocation =
      direction === 'from'
        ? previousLocations?.departureCity || {}
        : previousLocations?.arrivalCity || {};

    // if we are updating "from" city ensure to flush autocomplete results for "to" city
    const arrivalCity: ReturnType<typeof arrivalCitySelector> = arrivalCitySelector(state);
    if (direction === 'from' && arrivalCity.name) {
      dispatch(
        requestAutocomplete({
          query: arrivalCity.name,
          direction: 'to',
          sendDepartureCityId: true,
        }),
      );
    }

    let locationDetails;
    const cityId = location.uuid ? location.uuid : location.legacyId;

    //get city details
    const cityDetailsResponse: Awaited<ReturnType<typeof fetchCityRelationDetails>> =
      await fetchCityRelationDetails(citiesDetailsEndpoint, cityId, locale);
    if ('code' in cityDetailsResponse) {
      console.error(`Can't retrieve city for location`, location);
      dispatch(
        setLocation({ direction, location: previouslySelectedLocation as SearchMaskLocation }),
      );

      if (!isInit) {
        return;
      }
      locationDetails = location;
    } else {
      const [result] = cityDetailsResponse.map((c) => mapCityDetailResponse(c));
      locationDetails = {
        ...result,
        stationId: location.stationId,
        stationName: location.stationName,
      };
    }

    dispatch(updateAutocompleteValue({ direction, location: locationDetails }));

    //set products for relation
    const currentProductTypes: ReturnType<typeof currentProductTypesSelector> =
      currentProductTypesSelector(state);
    const currentProductTypesExtended: ReturnType<typeof currentProductTypesExtendedSelector> =
      currentProductTypesExtendedSelector(state);
    dispatch(resetAllProducts());
    dispatch(
      setRelationProducts({
        relationProducts: { ...currentProductTypes, ...currentProductTypesExtended },
      }),
    );

    //request search for autoupdate
    if (isAutoUpdate) {
      const isNewCity =
        (direction === 'from' && previousLocations?.departureCity.uuid !== locationDetails.uuid) ||
        (direction === 'to' && previousLocations?.arrivalCity.uuid !== locationDetails.uuid);

      const isNewStation =
        (direction === 'from' &&
          previousLocations?.departureCity.stationId !== locationDetails.stationId) ||
        (direction === 'to' &&
          previousLocations?.arrivalCity.stationId !== locationDetails.stationId);

      const isNew = isNewCity || isNewStation;

      if (isNew) {
        dispatch(requestSearch({}));
      }
    }
    const newLocation = {
      ...locationDetails,
      ...(showStationSuggestions
        ? {
            stationId: location.stationId,
            stationName: location.stationName,
          }
        : null),
    };
    dispatch(setLocation({ direction, location: newLocation }));
  };
